import { useState, useEffect } from "react";
import {
  Form,
  Radio,
  Select,
  Input,
  Space,
  Alert,
  Card,
  Checkbox,
  Button,
  Modal,
  Spin,
  Typography,
} from "antd";
import type { RadioChangeEvent } from "antd";
import { WizardStep } from "../WizardStep";
import {
  useBookingWizard,
  BookingState,
} from "../../../contexts/BookingWizardContext";
import { useCompanyDelivery } from "../../../hooks/useCompanyDelivery";
import { useCompany } from "../../../hooks/useCompany";
import { supabase } from "../../../lib/supabase";
import {
  DeleteOutlined,
  SearchOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import type { StorageOption } from "../../../utils/storageOptionUtils";
import { isStorageOption } from "../../../utils/storageOptionUtils";

const { TextArea } = Input;
const { Text } = Typography;

// Replace Google Maps interfaces with Geoapify interfaces
interface GeoapifyPrediction {
  // Update structure to match actual response
  datasource: {
    sourcename: string;
    attribution: string;
    license: string;
    url: string;
  };
  country: string;
  country_code: string;
  city?: string;
  postcode?: string;
  street?: string;
  housenumber?: string;
  formatted: string;
  address_line1?: string;
  address_line2?: string;
  lat: number;
  lon: number;
  place_id: string;
  result_type?: string;
}

// Add this new interface
interface SelectedAddressInfo {
  formatted: string;
  isSelected: boolean;
}

interface DeliveryFormValues {
  delivery_type: "storage" | "address";
  delivery_address_id?: number;
  name?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  street_name?: string;
  house_number?: string;
  instructions?: string;
  address_search?: string;
  saveForLater: boolean;
}

interface CompanyDeliveryInput {
  company_id: number;
  name: string;
  country: string;
  city: string;
  postal_code: string;
  street_name: string;
  house_number: string;
  instructions: string | null;
}

interface DeliveryDetails {
  street_name: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  instructions?: string;
}

export interface DeliveryDetailsStepProps {
  nextStepOverride?: number;
  onNextOverride?: () => void | Promise<void>;
  onPreviousOverride?: () => void | Promise<void>;
  productsStepOverride?: number;
  onGoToProductsOverride?: () => void;
  useV2Navigation?: boolean;
}

export const DeliveryDetails = ({
  nextStepOverride,
  onNextOverride,
  onPreviousOverride,
  productsStepOverride,
  onGoToProductsOverride,
}: DeliveryDetailsStepProps = {}) => {
  const { state, updateStep, setCurrentStep } = useBookingWizard();
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const {
    deliveryAddresses,
    loading,
    saveCompanyDelivery,
    deleteCompanyDelivery,
  } = useCompanyDelivery(selectedCompany);
  const [form] = Form.useForm<DeliveryFormValues>();
  const [isNewAddress, setIsNewAddress] = useState(false);

  // Track the currently selected address ID
  const [selectedAddressId, setSelectedAddressId] = useState<
    number | undefined
  >(
    deliveryAddresses?.length > 0 && !state.delivery_details
      ? deliveryAddresses[0].id
      : undefined
  );

  const [deliveryType, setDeliveryType] = useState<"storage" | "address">(
    state.delivery_type || "address" // Always default to "address" even if no saved addresses
  );

  // Storage option selected when delivery type is storage
  const [storageOption, setStorageOption] = useState<
    StorageOption | undefined
  >(state.storage_option ?? undefined);

  // Add state for validation errors
  const [storageValidationError, setStorageValidationError] = useState<
    string | null
  >(null);

  // Geoapify Address Autocomplete states
  const [addressSearchValue, setAddressSearchValue] = useState<string>("");
  const [predictions, setPredictions] = useState<GeoapifyPrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState<boolean>(false);
  const [isLoadingPredictions, setIsLoadingPredictions] =
    useState<boolean>(false);
  const [isLoadingPlaceDetails, setIsLoadingPlaceDetails] =
    useState<boolean>(false);
  const [manualAddressEntry, setManualAddressEntry] = useState<boolean>(false);

  // Add state to track if a selection is in progress to prevent re-fetching
  const [selectionInProgress, setSelectionInProgress] =
    useState<boolean>(false);

  // New state to track if address details should be shown
  const [showAddressDetails, setShowAddressDetails] = useState<boolean>(false);

  // State to track whether Geoapify API is available
  const [isApiAvailable, setIsApiAvailable] = useState<boolean>(true);

  // Track if we have a selected address to prevent re-searches
  const [selectedAddress, setSelectedAddress] =
    useState<SelectedAddressInfo | null>(null);

  // Check API availability on component mount
  useEffect(() => {
    const checkApiAvailability = async () => {
      try {
        const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;
        if (!apiKey || apiKey === "your_geoapify_api_key_here") {
          console.warn(
            "Missing or invalid Geoapify API key in environment variables"
          );
          setIsApiAvailable(false);
          return;
        }

        console.log(
          "Testing Geoapify API with key:",
          apiKey.substring(0, 5) + "..."
        );

        // Test API with a simple request
        await axios.get("https://api.geoapify.com/v1/geocode/autocomplete", {
          params: {
            text: "Amsterdam",
            format: "json",
            api_key: apiKey,
            lang: "en",
          },
        });

        console.log("Geoapify API test successful!");
        setIsApiAvailable(true);
      } catch (error) {
        console.error("Geoapify API unavailable:", error);

        if (axios.isAxiosError(error)) {
          console.log("API check error details:", {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url,
            params: error.config?.params,
          });
        }

        setIsApiAvailable(false);
      }
    };

    checkApiAvailability();
  }, []);

  // Get address predictions from Geoapify
  const fetchAddressPredictions = async (searchText: string) => {
    // If we already selected this address, don't search again
    if (
      selectedAddress &&
      selectedAddress.formatted === searchText &&
      selectedAddress.isSelected
    ) {
      console.log("Skipping search for already selected address:", searchText);
      return;
    }

    if (!searchText || searchText.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    setIsLoadingPredictions(true);

    try {
      // Check if API key is available
      const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;
      if (!apiKey || apiKey === "your_geoapify_api_key_here") {
        throw new Error(
          "Missing or invalid Geoapify API key. Please update your .env file."
        );
      }

      // Get the current language from i18next - simplify to just the base language code
      const fullLang = localStorage.getItem("i18nextLng") || "en";
      const language = fullLang.split("-")[0]; // Convert 'en-GB' to just 'en'

      // Log request params for debugging
      console.log("Geoapify API request params:", {
        text: searchText,
        api_key: apiKey ? `${apiKey.substring(0, 5)}...` : "missing", // Only show start of key for security
        lang: language,
      });

      // Make request to Geoapify API
      console.log(
        `Fetching address predictions for: "${searchText}" in language: ${language}`
      );
      const response = await axios.get(
        "https://api.geoapify.com/v1/geocode/autocomplete",
        {
          params: {
            text: searchText,
            format: "json",
            api_key: apiKey, // Changed from apiKey to api_key
            lang: language,
          },
        }
      );

      // Debug response
      console.log("Geoapify API response:", response.data);

      // Update to handle the correct response format
      if (response.data && response.data.results) {
        console.log(`Found ${response.data.results.length} predictions`);
        setPredictions(response.data.results);
        setShowPredictions(true);
      } else {
        console.log("No predictions found in response");
        setPredictions([]);
        setShowPredictions(false);
      }
    } catch (error) {
      console.error("Error fetching address predictions:", error);
      // Show more user-friendly error message
      let errorMessage = "Error fetching address predictions";

      if (axios.isAxiosError(error)) {
        console.log("Axios error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          params: error.config?.params,
        });
        if (error.response?.status === 401) {
          errorMessage =
            "Unauthorized: Please check your Geoapify API key in the .env file";
        } else if (error.response?.status === 429) {
          errorMessage =
            "Too many requests: You've reached the Geoapify API rate limit";
        } else if (error.response?.status === 400) {
          errorMessage = `Bad request: ${
            error.response?.data?.message || "Check the API parameters"
          }`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Display error message to user
      Modal.error({
        title: "Address Search Error",
        content: errorMessage,
      });

      setPredictions([]);
      setShowPredictions(false);
    } finally {
      setIsLoadingPredictions(false);
    }
  };

  // Handle address prediction selection
  const handlePredictionSelect = (prediction: GeoapifyPrediction) => {
    // Temporarily store formatted address
    const formattedAddress = prediction.formatted;

    // Mark this address as selected
    setSelectedAddress({
      formatted: formattedAddress,
      isSelected: true,
    });

    // Prevent searching again when selecting an address
    setSelectionInProgress(true);

    // First, set showPredictions to false to hide dropdown
    setShowPredictions(false);
    setPredictions([]); // Clear predictions to prevent dropdown from showing again
    setShowAddressDetails(true); // Show address fields when an address is selected

    // Set as new address
    setIsNewAddress(true);

    // Extract address details from the prediction
    const addressData = {
      delivery_address_id: -1, // Set to add new address
      name: formattedAddress,
      country: prediction.country || "",
      city: prediction.city || "",
      postal_code: prediction.postcode || "",
      street_name: prediction.street || "",
      house_number: prediction.housenumber || "",
    };

    // Update form with address details
    form.setFieldsValue(addressData);
    setIsLoadingPlaceDetails(false);

    // Use a separate state variable to prevent triggering the search again
    // Set this AFTER selection is in progress to avoid re-fetch
    setTimeout(() => {
      setAddressSearchValue(formattedAddress);
    }, 10);

    // Reset selection flag after a longer delay
    // This ensures the value is set before we allow new searches
    setTimeout(() => {
      setSelectionInProgress(false);
    }, 1000);
  };

  // Handle manual address entry
  const handleManualAddressEntry = () => {
    setManualAddressEntry(true);
    setPredictions([]);
    setShowPredictions(false);
    setAddressSearchValue("");
    setSelectedAddress(null); // Reset selected address
    setShowAddressDetails(true); // Show address fields when entering manually

    // Focus on the name field
    setTimeout(() => {
      const nameInput = document.querySelector('input[id="name"]');
      if (nameInput) {
        (nameInput as HTMLInputElement).focus();
      }
    }, 100);
  };

  // Fetch address predictions when search value changes
  useEffect(() => {
    // Skip fetching completely if we have a selected address
    if (
      selectedAddress &&
      selectedAddress.formatted === addressSearchValue &&
      selectedAddress.isSelected
    ) {
      console.log("Using selected address, skipping fetch");
      return;
    }

    // Skip fetching if selection is in progress
    if (selectionInProgress) {
      console.log("Selection in progress, skipping fetch");
      return;
    }

    const timeoutId = setTimeout(() => {
      if (addressSearchValue && addressSearchValue.length >= 3) {
        fetchAddressPredictions(addressSearchValue);
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 400); // Increase debounce time to avoid quick re-fetching

    return () => clearTimeout(timeoutId);
  }, [addressSearchValue, selectionInProgress, selectedAddress]);

  // Debug: log predictions when they change
  useEffect(() => {
    if (predictions.length > 0) {
      console.log("Current predictions:", predictions);
      console.log("showPredictions state:", showPredictions);

      // Log a sample prediction to help debug the structure
      console.log(
        "Sample prediction structure:",
        JSON.stringify(predictions[0], null, 2)
      );
    }
  }, [predictions, showPredictions]);

  // Auto-select "create a new address" and show address search when no saved addresses
  useEffect(() => {
    // Only run this effect when component mounts and delivery addresses are loaded
    if (!loading) {
      // Check if delivery type is address and there are no saved addresses
      if (deliveryType === "address" && deliveryAddresses?.length === 0) {
        console.log("No saved addresses found, setting up new address entry");

        // Set form values for new address
        form.setFieldsValue({
          delivery_type: "address",
          delivery_address_id: -1,
        });

        // Set state for new address
        setIsNewAddress(true);
        setSelectedAddressId(undefined);

        // Don't immediately show manual entry form, but let user search
        setManualAddressEntry(false);

        // Focus on the address search field
        setTimeout(() => {
          const searchInput = document.querySelector(
            'input[placeholder="' +
              t("bookingWizard.deliveryDetails.searchAddressPlaceholder") +
              '"]'
          );
          if (searchInput) {
            (searchInput as HTMLInputElement).focus();
          }
        }, 300);
      }
    }
  }, [loading, deliveryAddresses, deliveryType, form, t]);

  // Initialize form with existing delivery data
  useEffect(() => {
    // If we have delivery addresses and no delivery type is set yet, default to address
    if (!state.delivery_type && deliveryAddresses?.length > 0) {
      setDeliveryType("address");
      setShowAddressDetails(false); // No manual entry needed, showing saved address
      setSelectedAddressId(deliveryAddresses[0].id); // Set the first address as selected
      form.setFieldsValue({
        delivery_type: "address",
        delivery_address_id: deliveryAddresses[0].id,
        name: deliveryAddresses[0].name,
        country: deliveryAddresses[0].country,
        city: deliveryAddresses[0].city,
        postal_code: deliveryAddresses[0].postal_code,
        street_name: deliveryAddresses[0].street_name,
        house_number: deliveryAddresses[0].house_number,
        instructions: deliveryAddresses[0].instructions || undefined,
      });
      return;
    }

    if (state.delivery_type) {
      setDeliveryType(state.delivery_type);
      form.setFieldsValue({
        delivery_type: state.delivery_type,
      });

      const deliveryDetails = state.delivery_details as
        | DeliveryDetails
        | undefined;

      if (state.delivery_type === "address" && deliveryDetails) {
        // Find matching saved address if it exists
        const matchingAddress = deliveryAddresses.find(
          (addr) =>
            addr.street_name === deliveryDetails.street_name &&
            addr.house_number === deliveryDetails.house_number &&
            addr.postal_code === deliveryDetails.postal_code &&
            addr.city === deliveryDetails.city &&
            addr.country === deliveryDetails.country
        );

        if (matchingAddress) {
          // If we found a matching saved address, use it
          setIsNewAddress(false);
          setSelectedAddressId(matchingAddress.id); // Set the matching address as selected
          setShowAddressDetails(false); // Using existing address
          form.setFieldsValue({
            delivery_address_id: matchingAddress.id,
            name: matchingAddress.name,
            country: matchingAddress.country,
            city: matchingAddress.city,
            postal_code: matchingAddress.postal_code,
            street_name: matchingAddress.street_name,
            house_number: matchingAddress.house_number,
            instructions: matchingAddress.instructions || undefined,
          });
        } else {
          // If no matching address found, treat as new address
          setIsNewAddress(true);
          setSelectedAddressId(undefined); // No saved address is selected
          setShowAddressDetails(true); // Show fields for editing since we have data
          form.setFieldsValue({
            delivery_address_id: -1,
            name: deliveryDetails.street_name, // Use street name as name for new address
            country: deliveryDetails.country,
            city: deliveryDetails.city,
            postal_code: deliveryDetails.postal_code,
            street_name: deliveryDetails.street_name,
            house_number: deliveryDetails.house_number,
            instructions: deliveryDetails.instructions || undefined,
          });
        }
      } else if (state.delivery_type === "address") {
        // If delivery type is address but no details, prepare for new address
        setIsNewAddress(true);
        setSelectedAddressId(undefined); // No saved address is selected
        setShowAddressDetails(false); // Hide fields initially
      } else if (state.delivery_type === "storage") {
        if (state.storage_option && isStorageOption(state.storage_option)) {
          setStorageOption(state.storage_option);
        }

        // If delivery type is storage, validate EAN requirements
        const productsWithoutEAN =
          state.products?.filter(
            (product) => !product.ean || product.ean.toString().length !== 13
          ) || [];

        if (productsWithoutEAN.length > 0) {
          setStorageValidationError(
            t(
              "bookingWizard.deliveryDetails.storageValidation.eanRequiredForStorage"
            )
          );
        }
      }
    }
  }, [
    state.delivery_type,
    state.delivery_details,
    state.storage_option,
    form,
    deliveryAddresses,
    state.products,
    t,
  ]);

  // Validation function for storage options
  const validateStorageOptions = (): boolean => {
    if (deliveryType === "storage") {
      // First check if all products have valid EANs
      const productsWithoutEAN =
        state.products?.filter(
          (product) => !product.ean || product.ean.toString().length !== 13
        ) || [];

      if (productsWithoutEAN.length > 0) {
        setStorageValidationError(
          t(
            "bookingWizard.deliveryDetails.storageValidation.eanRequiredForStorage"
          )
        );
        return false;
      }

      if (!storageOption) {
        setStorageValidationError(
          t("bookingWizard.deliveryDetails.storageValidation.optionRequired")
        );
        return false;
      }
    }

    setStorageValidationError(null);
    return true;
  };

  const handleNext = async () => {
    try {
      const values = await form.validateFields();

      // Validate storage options before proceeding
      if (!validateStorageOptions()) {
        return;
      }

      // Prepare the state update data
      const stateUpdateData: Partial<BookingState> = {
        delivery_type: values.delivery_type,
      };

      // Check for existing booking_delivery record
      const { data: existingDelivery } = await supabase
        .from("booking_delivery")
        .select("id")
        .eq("booking_id", state.id)
        .maybeSingle();

      if (values.delivery_type === "storage") {
        // If switching to storage, remove any existing delivery details
        if (existingDelivery) {
          const { error: deleteError } = await supabase
            .from("booking_delivery")
            .delete()
            .eq("id", existingDelivery.id);

          if (deleteError) throw deleteError;
        }
        // Clear delivery details from state
        stateUpdateData.delivery_details = undefined;
        stateUpdateData.storage_option = storageOption ?? null;
      } else if (values.delivery_type === "address") {
        stateUpdateData.storage_option = null;
        let addressData: {
          country: string;
          city: string;
          postal_code: string;
          street_name: string;
          house_number: string;
          instructions: string | null;
        };

        if (!isNewAddress && values.delivery_address_id) {
          // Use selected saved address
          const selectedAddress = deliveryAddresses.find(
            (addr) => addr.id === values.delivery_address_id
          );
          if (!selectedAddress) {
            throw new Error("Selected address not found");
          }
          addressData = {
            country: selectedAddress.country,
            city: selectedAddress.city,
            postal_code: selectedAddress.postal_code,
            street_name: selectedAddress.street_name,
            house_number: selectedAddress.house_number,
            instructions: selectedAddress.instructions,
          };
        } else {
          // Use form values for new address
          if (
            !values.country ||
            !values.city ||
            !values.postal_code ||
            !values.street_name ||
            !values.house_number
          ) {
            throw new Error("Missing required delivery fields");
          }
          addressData = {
            country: values.country,
            city: values.city,
            postal_code: values.postal_code,
            street_name: values.street_name,
            house_number: values.house_number,
            instructions: values.instructions || null,
          };

          // Save new address to company_delivery if requested
          if (isNewAddress && values.saveForLater && selectedCompany) {
            const deliveryData: CompanyDeliveryInput = {
              company_id: selectedCompany,
              name: values.name!,
              country: addressData.country,
              city: addressData.city,
              postal_code: addressData.postal_code,
              street_name: addressData.street_name,
              house_number: addressData.house_number,
              instructions: addressData.instructions,
            };
            await saveCompanyDelivery(deliveryData);
          }
        }

        let bookingDelivery;
        if (existingDelivery) {
          // Update existing record
          const { data, error } = await supabase
            .from("booking_delivery")
            .update({
              ...addressData,
              booking_id: state.id,
            })
            .eq("id", existingDelivery.id)
            .select()
            .single();

          if (error) throw error;
          bookingDelivery = data;
        } else {
          // Create new record
          const { data, error } = await supabase
            .from("booking_delivery")
            .insert({
              ...addressData,
              booking_id: state.id,
            })
            .select()
            .single();

          if (error) throw error;
          bookingDelivery = data;
        }

        // Update state with delivery details
        stateUpdateData.delivery_details = {
          id: bookingDelivery.id,
          country: bookingDelivery.country,
          city: bookingDelivery.city,
          postal_code: bookingDelivery.postal_code,
          street_name: bookingDelivery.street_name,
          house_number: bookingDelivery.house_number,
          instructions: bookingDelivery.instructions,
        };
      }

      // Update booking state
      await updateStep(nextStepOverride ?? 5, stateUpdateData);
      if (onNextOverride) {
        await onNextOverride();
      }
    } catch (error) {
      console.error("Failed to save delivery details:", error);
    }
  };

  const handleDeliveryTypeChange = async (e: RadioChangeEvent) => {
    const value = e.target.value as "storage" | "address";
    setDeliveryType(value);

    // Clear validation error when changing delivery type
    setStorageValidationError(null);

    // Persist delivery type immediately so pricing updates
    // Use currentStep to stay on the same step, don't advance
    try {
      await updateStep(state.currentStep, {
        delivery_type: value,
        ...(value === "address" ? { storage_option: null } : {}),
      });
    } catch (error) {
      console.error("Failed to update delivery type:", error);
    }

    // If switching to storage, validate EAN requirements immediately
    if (value === "storage") {
      // Check if all products have valid EANs
      const productsWithoutEAN =
        state.products?.filter(
          (product) => !product.ean || product.ean.toString().length !== 13
        ) || [];

      if (productsWithoutEAN.length > 0) {
        setStorageValidationError(
          t(
            "bookingWizard.deliveryDetails.storageValidation.eanRequiredForStorage"
          )
        );
      }
    }

    // If switching to storage, clear address fields
    if (value === "storage") {
      form.setFieldsValue({
        delivery_address_id: undefined,
        name: undefined,
        country: undefined,
        city: undefined,
        postal_code: undefined,
        street_name: undefined,
        house_number: undefined,
        instructions: undefined,
        saveForLater: false,
      });
      setStorageOption(undefined);
      setIsNewAddress(false);
      setManualAddressEntry(false);
      setAddressSearchValue("");
      setPredictions([]);
      setShowPredictions(false);
      setSelectedAddress(null); // Reset selected address
      setSelectedAddressId(undefined); // Clear selected address ID
      setShowAddressDetails(false); // Hide address fields when switching to storage
    } else if (value === "address" && deliveryAddresses?.length > 0) {
      // If switching to address and we have saved addresses, select the first one
      const firstAddress = deliveryAddresses[0];
      setIsNewAddress(false);
      setManualAddressEntry(false);
      setSelectedAddressId(firstAddress.id); // Set the first address as selected
      setShowAddressDetails(false); // Hide manual address fields when using saved address
      form.setFieldsValue({
        delivery_address_id: firstAddress.id,
        name: firstAddress.name,
        country: firstAddress.country,
        city: firstAddress.city,
        postal_code: firstAddress.postal_code,
        street_name: firstAddress.street_name,
        house_number: firstAddress.house_number,
        instructions: firstAddress.instructions || undefined,
      });
    } else {
      // If switching to address and no saved addresses, prepare for new address
      setIsNewAddress(true);
      setManualAddressEntry(false);
      setSelectedAddressId(undefined); // No saved address is selected
      setShowAddressDetails(false); // Hide fields initially, user needs to search or select manual entry
      form.setFieldsValue({
        delivery_address_id: -1,
        name: undefined,
        country: undefined,
        city: undefined,
        postal_code: undefined,
        street_name: undefined,
        house_number: undefined,
        instructions: undefined,
        saveForLater: false,
      });
    }
  };

  const handleStorageOptionChange = (e: RadioChangeEvent) => {
    const value = e.target.value as StorageOption;
    setStorageOption(value);
    setStorageValidationError(null);
  };

  const handleAddressSelect = (value: number) => {
    if (value === -1) {
      setIsNewAddress(true);
      setSelectedAddressId(undefined); // Clear selected address ID
      // Clear form fields for new address
      form.setFieldsValue({
        name: undefined,
        country: undefined,
        city: undefined,
        postal_code: undefined,
        street_name: undefined,
        house_number: undefined,
        instructions: undefined,
        saveForLater: false,
      });

      // Reset address search state
      setManualAddressEntry(false);
      setAddressSearchValue("");
      setPredictions([]);
      setShowPredictions(false);
      setSelectedAddress(null); // Reset selected address
      setShowAddressDetails(false); // Hide address fields initially
    } else {
      setIsNewAddress(false);
      setSelectedAddressId(value); // Set the selected address ID
      setManualAddressEntry(false);
      setShowAddressDetails(true); // Show address fields when selecting existing address
      setSelectedAddress(null); // Reset selected address state

      const savedAddress = deliveryAddresses.find((addr) => addr.id === value);
      if (savedAddress) {
        // Immediately set all form values for the selected address
        form.setFieldsValue({
          delivery_type: "address",
          delivery_address_id: savedAddress.id,
          name: savedAddress.name,
          country: savedAddress.country,
          city: savedAddress.city,
          postal_code: savedAddress.postal_code,
          street_name: savedAddress.street_name,
          house_number: savedAddress.house_number,
          instructions: savedAddress.instructions || undefined,
        });
      }
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    // Find the address to get its name for the confirmation message
    const addressToDelete = deliveryAddresses.find(
      (addr) => addr.id === addressId
    );
    if (!addressToDelete) return;

    Modal.confirm({
      title: t("bookingWizard.deliveryDetails.deleteAddress"),
      content: t("bookingWizard.deliveryDetails.confirmDeleteAddress"),
      okText: t("common.yes"),
      okType: "danger",
      cancelText: t("common.no"),
      onOk: async () => {
        try {
          await deleteCompanyDelivery(addressId);
          Modal.success({
            content: t("bookingWizard.deliveryDetails.addressDeletedSuccess"),
          });

          // Reset form to initial state
          form.resetFields();

          // Set delivery type to "storage" as default
          form.setFieldsValue({
            delivery_type: "storage",
            delivery_address_id: -1,
            saveForLater: false,
          });

          // Reset any existing delivery details in the booking state
          if (state.delivery_details) {
            await updateStep(state.currentStep, {
              delivery_type: "storage",
              delivery_details: undefined,
            });
          }
        } catch (error) {
          console.error("Failed to delete address:", error);
          Modal.error({
            title: "Error",
            content: t("bookingWizard.deliveryDetails.addressDeleteError"),
          });
        }
      },
    });
  };

  // Determine if Next button should be disabled
  const isNextDisabled = (() => {
    if (deliveryType === "storage") {
      // Check if there are products without valid EANs
      const productsWithoutEAN =
        state.products?.filter(
          (product) => !product.ean || product.ean.toString().length !== 13
        ) || [];

      // Disable if there are products without EANs
      if (productsWithoutEAN.length > 0) {
        return true;
      }

      // Disable if no storage option selected
      return !storageOption;
    }
    return false;
  })();

  const handlePrevious = () => {
    if (onPreviousOverride) {
      onPreviousOverride();
      return;
    }
    updateStep(state.currentStep - 1, {});
  };

  const handleGoToProducts = () => {
    if (onGoToProductsOverride) {
      onGoToProductsOverride();
      return;
    }
    updateStep(productsStepOverride ?? 2, {});
  };

  return (
    <WizardStep
      onNext={handleNext}
      onPrevious={handlePrevious}
      nextDisabled={isNextDisabled}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          delivery_type: deliveryType,
          delivery_address_id:
            deliveryAddresses?.length > 0 ? deliveryAddresses[0].id : -1, // Use -1 for new address when no saved addresses
          saveForLater: false,
        }}
      >
        <Form.Item
          name="delivery_type"
          label={t("bookingWizard.deliveryDetails.deliveryType")}
          rules={[
            {
              required: true,
              message: t("bookingWizard.deliveryDetails.deliveryTypeRequired"),
            },
          ]}
        >
          <Radio.Group onChange={handleDeliveryTypeChange}>
            <Space direction="vertical">
              <Radio value="storage">
                {t("bookingWizard.deliveryDetails.storage")}
              </Radio>
              <Radio value="address">
                {t("bookingWizard.deliveryDetails.address")}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {deliveryType === "storage" ? (
          <Card title={t("bookingWizard.deliveryDetails.storageTitle")}>
            {/* Check if there are products without valid EANs */}
            {(() => {
              const productsWithoutEAN =
                state.products?.filter(
                  (product) =>
                    !product.ean || product.ean.toString().length !== 13
                ) || [];

              if (productsWithoutEAN.length > 0) {
                // Show only the error message with product list, no storage options
                return (
                  <div>
                    <Alert
                      message={t(
                        "bookingWizard.deliveryDetails.storageValidation.eanRequiredForStorage"
                      )}
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    {/* Show list of products with EAN status */}
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 8, color: "#666" }}>
                        {t("bookingWizard.deliveryDetails.productsEanStatus")}
                      </h4>
                      <div
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                          padding: "12px",
                        }}
                      >
                        {state.products?.map((product, index) => {
                          const hasValidEAN =
                            product.ean && product.ean.toString().length === 13;
                          return (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom:
                                  index < (state.products?.length || 0) - 1
                                    ? "1px solid #f0f0f0"
                                    : "none",
                              }}
                            >
                              <div>
                                <strong>{product.name}</strong>
                                {product.ean && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#666",
                                      marginTop: "2px",
                                    }}
                                  >
                                    EAN: {product.ean}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {hasValidEAN ? (
                                  <span
                                    style={{
                                      color: "#52c41a",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ✓{" "}
                                    {t(
                                      "bookingWizard.deliveryDetails.validEan"
                                    )}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#ff4d4f",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ✗{" "}
                                    {t(
                                      "bookingWizard.deliveryDetails.missingEan"
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <Button
                      type="primary"
                      onClick={handleGoToProducts}
                      style={{ width: "100%" }}
                    >
                      {t("bookingWizard.deliveryDetails.goToProducts")}
                    </Button>
                  </div>
                );
              }

              // Show storage options only if all products have valid EANs
              return (
                <>
                  <Form.Item
                    label={t(
                      "bookingWizard.deliveryDetails.storageOptionsLabel"
                    )}
                  >
                    <Radio.Group
                      value={storageOption}
                      onChange={handleStorageOptionChange}
                    >
                      <Space direction="vertical">
                        <Radio value="lvb_storage">
                          {t(
                            "bookingWizard.deliveryDetails.storageOptions.lvbStorage"
                          )}
                        </Radio>
                        <Radio value="fulfilment_efreight">
                          {t(
                            "bookingWizard.deliveryDetails.storageOptions.fulfilmentEfreight"
                          )}
                        </Radio>
                        <Radio value="contains_batteries">
                          {t(
                            "bookingWizard.deliveryDetails.storageOptions.containsBatteries"
                          )}
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>

                  {/* Display validation error if any (for other storage validation issues) */}
                  {storageValidationError &&
                    !storageValidationError.includes("EAN") && (
                      <Alert
                        message={storageValidationError}
                        type="error"
                        showIcon
                        style={{ marginTop: 16 }}
                      />
                    )}

                  {/* Show warehouse message when fulfilment or LVB storage is selected */}
                  {(storageOption === "fulfilment_efreight" ||
                    storageOption === "lvb_storage") && (
                    <Alert
                      message={t("bookingWizard.warehouseStorageMessage")}
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </>
              );
            })()}
          </Card>
        ) : (
          <Card title={t("bookingWizard.deliveryDetails.title")}>
            <Form.Item
              name="delivery_address_id"
              label={t("bookingWizard.deliveryDetails.selectAddress")}
              rules={[
                {
                  required: true,
                  message: t("bookingWizard.deliveryDetails.addressRequired"),
                },
              ]}
            >
              <Select
                loading={loading}
                onChange={handleAddressSelect}
                placeholder={t("bookingWizard.deliveryDetails.selectAddress")}
                notFoundContent={t(
                  "bookingWizard.deliveryDetails.noSavedAddresses"
                )}
                options={[
                  ...(deliveryAddresses?.map((addr) => ({
                    label: (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{addr.name}</span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAddress(addr.id);
                          }}
                        />
                      </div>
                    ),
                    value: addr.id,
                  })) || []),
                  {
                    label: t("bookingWizard.deliveryDetails.addNewAddress"),
                    value: -1,
                  },
                ]}
              />
            </Form.Item>

            {/* Display saved address card when a saved address is selected */}
            {!isNewAddress &&
              ((selectedAddressId !== undefined && selectedAddressId !== -1) ||
                (form.getFieldValue("delivery_address_id") !== undefined &&
                  form.getFieldValue("delivery_address_id") !== -1)) && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, background: "#f5f5f5" }}
                >
                  {deliveryAddresses
                    .filter(
                      (addr) =>
                        addr.id === selectedAddressId ||
                        addr.id === form.getFieldValue("delivery_address_id")
                    )
                    .map((addr) => (
                      <div key={addr.id}>
                        <div style={{ marginBottom: 8 }}>
                          <strong>
                            {addr.street_name} {addr.house_number}
                          </strong>
                        </div>
                        <div style={{ color: "#666" }}>{addr.postal_code}</div>
                        <div style={{ color: "#666" }}>
                          {addr.city}, {addr.country}
                        </div>
                        {addr.instructions && (
                          <div
                            style={{
                              marginTop: 8,
                              fontStyle: "italic",
                              color: "#666",
                            }}
                          >
                            {t("bookingWizard.deliveryDetails.instructions")}:{" "}
                            {addr.instructions}
                          </div>
                        )}
                      </div>
                    ))}
                </Card>
              )}

            {/* Show the address search and form fields only for new addresses */}
            {isNewAddress && (
              <>
                {/* Address Search with Geoapify Address Autocomplete */}
                {!manualAddressEntry && (
                  <div style={{ marginBottom: 24 }}>
                    {/* API availability warning */}
                    {!isApiAvailable && (
                      <Alert
                        message={t(
                          "bookingWizard.deliveryDetails.apiUnavailable"
                        )}
                        description={t(
                          "bookingWizard.deliveryDetails.manualEntryNeeded"
                        )}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        action={
                          <Button
                            onClick={handleManualAddressEntry}
                            type="primary"
                          >
                            {t("bookingWizard.deliveryDetails.enterManually")}
                          </Button>
                        }
                      />
                    )}

                    {/* Address search field */}
                    {isApiAvailable && (
                      <Form.Item
                        label={t("bookingWizard.deliveryDetails.searchAddress")}
                        required={true}
                      >
                        <div style={{ position: "relative" }}>
                          <Input
                            placeholder={t(
                              "bookingWizard.deliveryDetails.searchAddressPlaceholder"
                            )}
                            value={addressSearchValue}
                            onChange={(e) =>
                              setAddressSearchValue(e.target.value)
                            }
                            suffix={
                              isLoadingPredictions ? (
                                <Spin size="small" />
                              ) : (
                                <SearchOutlined />
                              )
                            }
                            style={{ marginBottom: 8 }}
                          />

                          {/* Predictions dropdown from Geoapify */}
                          {showPredictions && predictions.length > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                width: "100%",
                                maxHeight: "300px",
                                overflowY: "auto",
                                background: "#fff",
                                border: "2px solid #1890ff",
                                borderRadius: "4px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                zIndex: 1050,
                                marginTop: "2px",
                              }}
                            >
                              {predictions.map((prediction, index) => {
                                console.log(
                                  `Rendering prediction ${index}:`,
                                  prediction.formatted
                                );
                                return (
                                  <div
                                    key={prediction.place_id || index}
                                    style={{
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      backgroundColor: "#ffffff",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onClick={() =>
                                      handlePredictionSelect(prediction)
                                    }
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <EnvironmentOutlined
                                      style={{
                                        marginRight: 8,
                                        color: "#1890ff",
                                      }}
                                    />
                                    <span>{prediction.formatted}</span>
                                  </div>
                                );
                              })}

                              {/* Manual entry option in dropdown */}
                              <div
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderTop: "1px solid #f0f0f0",
                                  color: "#1890ff",
                                  fontWeight: 500,
                                  backgroundColor: "#f9f9f9",
                                }}
                                onClick={handleManualAddressEntry}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                {t(
                                  "bookingWizard.deliveryDetails.enterManually"
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </Form.Item>
                    )}

                    {/* Loading indicator */}
                    {isLoadingPlaceDetails && (
                      <div style={{ textAlign: "center", padding: "16px 0" }}>
                        <Spin />
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">
                            {t("bookingWizard.deliveryDetails.loadingAddress")}
                          </Text>
                        </div>
                      </div>
                    )}

                    {/* Manual entry link */}
                    {isApiAvailable && (
                      <div style={{ marginTop: "-4px" }}>
                        <Button
                          type="link"
                          onClick={handleManualAddressEntry}
                          style={{ padding: 0 }}
                        >
                          {t("bookingWizard.deliveryDetails.enterManually")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Show address details only when showAddressDetails is true */}
                {(showAddressDetails || manualAddressEntry) && (
                  <>
                    <Form.Item
                      name="name"
                      label={t("bookingWizard.deliveryDetails.addressName")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.addressNameRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.deliveryDetails.addressName"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      name="country"
                      label={t("bookingWizard.deliveryDetails.country")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.countryRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("bookingWizard.deliveryDetails.country")}
                      />
                    </Form.Item>

                    <Form.Item
                      name="city"
                      label={t("bookingWizard.deliveryDetails.city")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.cityRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("bookingWizard.deliveryDetails.city")}
                      />
                    </Form.Item>

                    <Form.Item
                      name="postal_code"
                      label={t("bookingWizard.deliveryDetails.postalCode")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.postalCodeRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.deliveryDetails.postalCode"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      name="street_name"
                      label={t("bookingWizard.deliveryDetails.streetName")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.streetNameRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.deliveryDetails.streetName"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      name="house_number"
                      label={t("bookingWizard.deliveryDetails.houseNumber")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.deliveryDetails.houseNumberRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.deliveryDetails.houseNumber"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      name="instructions"
                      label={t("bookingWizard.deliveryDetails.instructions")}
                    >
                      <TextArea
                        rows={4}
                        placeholder={t(
                          "bookingWizard.deliveryDetails.instructions"
                        )}
                      />
                    </Form.Item>

                    <Form.Item name="saveForLater" valuePropName="checked">
                      <Checkbox>
                        {t("bookingWizard.deliveryDetails.saveForLater")}
                      </Checkbox>
                    </Form.Item>
                  </>
                )}
              </>
            )}
          </Card>
        )}
      </Form>
    </WizardStep>
  );
};
